import React, { useState, useEffect } from 'react';
import supabase from '../../../supabase/supabase';
import { FiActivity, FiServer, FiDatabase, FiClock, FiCheckCircle, FiXCircle } from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

const GatewayStatus = () => {
  const [status, setStatus] = useState(null);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    // Use .maybeSingle() to prevent 406 errors when tables are empty
    const { data: gateway } = await supabase.from('payment_gateways').select('*').eq('name', 'marx_ipg').maybeSingle();
    const { data: lastLog } = await supabase.from('payment_logs').select('created_at').eq('status_code', 200).order('created_at', { ascending: false }).limit(1).maybeSingle();
    const { data: lastHook } = await supabase.from('payment_webhooks').select('created_at').order('created_at', { ascending: false }).limit(1).maybeSingle();

    setStatus({
      gateway: gateway || null,
      lastLog: lastLog?.created_at,
      lastHook: lastHook?.created_at
    });
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    const start = performance.now();
    
    try {
      // Explicitly pass the user's JWT to ensure the Edge Function authorizes correctly
      const { data: { session } } = await supabase.auth.getSession();
      const { data, error } = await supabase.functions.invoke('marx-test-connection', {
        headers: {
          Authorization: `Bearer ${session?.access_token}`
        }
      });

      const time = Math.round(performance.now() - start);
      if (error) throw error;
      setTestResult({ ...data, time });
    } catch (err) {
      const time = Math.round(performance.now() - start);
      setTestResult({ success: false, error: err.message || 'Network Error', time });
    } finally {
      setTesting(false);
    }
  };

  if (!status) return <div className="p-4 text-gray-500">Loading status...</div>;

  const config = status.gateway?.config || {};
  const merchantMasked = config.merchant_id ? `***${config.merchant_id.slice(-4)}` : 'Not Configured';

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
          <h3 className="text-lg font-serif text-gray-800 mb-4 flex items-center gap-2"><SafeIcon icon={FiServer} className="text-brand-gold"/> Gateway Configuration</h3>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between"><span className="text-gray-500">Current Gateway</span><span className="font-bold text-gray-800">Marx IPG v4</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Environment</span><span className="font-bold text-gray-800 capitalize">{config.environment || 'Unknown'}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Gateway Enabled</span><span className={`font-bold ${status.gateway?.is_active ? 'text-green-600' : 'text-red-500'}`}>{status.gateway?.is_active ? 'Yes' : 'No'}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Merchant ID</span><span className="font-mono text-gray-800">{merchantMasked}</span></div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
          <h3 className="text-lg font-serif text-gray-800 mb-4 flex items-center gap-2"><SafeIcon icon={FiDatabase} className="text-brand-gold"/> System Health</h3>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between"><span className="text-gray-500">Database Status</span><span className="font-bold text-green-600">Healthy</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Edge Functions</span><span className="font-bold text-green-600">Deployed</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Last Successful API Call</span><span className="text-gray-800">{status.lastLog ? new Date(status.lastLog).toLocaleString() : 'Never'}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Last Webhook Received</span><span className="text-gray-800">{status.lastHook ? new Date(status.lastHook).toLocaleString() : 'Never'}</span></div>
          </div>
        </div>
      </div>

      <div className="bg-brand-sand/30 p-6 rounded-2xl border border-brand-darkSage/10">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-lg font-serif text-brand-darkSage">Test Gateway Connection</h3>
            <p className="text-xs text-brand-lightText">Verifies Merchant ID, API Key, and Marx API connectivity.</p>
          </div>
          <button 
            onClick={handleTestConnection} 
            disabled={testing}
            className="bg-brand-darkSage text-white px-6 py-2.5 rounded-xl text-xs font-bold uppercase tracking-widest hover:bg-brand-gold transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            <SafeIcon icon={FiActivity} /> {testing ? 'Testing...' : 'Test Connection'}
          </button>
        </div>

        {testResult && (
          <div className={`p-4 rounded-xl border ${testResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
            <div className="flex items-center gap-2 font-bold mb-2 text-sm">
              <SafeIcon icon={testResult.success ? FiCheckCircle : FiXCircle} className={testResult.success ? 'text-green-600' : 'text-red-600'} />
              <span className={testResult.success ? 'text-green-800' : 'text-red-800'}>
                {testResult.success ? 'Connection Successful' : 'Connection Failed'}
              </span>
              <span className="ml-auto text-xs font-mono text-gray-500">{testResult.time}ms</span>
            </div>
            <p className="text-sm text-gray-700">{testResult.message || testResult.error}</p>
            {testResult.details && (
              <pre className="mt-2 text-[10px] overflow-x-auto bg-white/50 p-2 rounded border border-black/10 text-gray-800">
                {JSON.stringify(testResult.details, null, 2)}
              </pre>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default GatewayStatus;